/**
 * Timesheet API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  CreateTimesheetRequest,
  EmployeeRate,
  LaborReport,
  LaborReportParams,
  SetEmployeeRateRequest,
  Timesheet,
  TimesheetListParams,
  TimesheetListResponse,
  TimesheetStats,
  UpdateTimesheetRequest,
} from '../types/timesheet';

/**
 * Timesheet API Endpoints
 * Base URL: /projects/:projectId/timesheets
 */
export const timesheetApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ==========================================================================
    // TIMESHEET CRUD
    // ==========================================================================

    // Get all timesheets for a project
    getTimesheets: builder.query<
      ApiResponse<TimesheetListResponse>,
      { projectId: string; params?: TimesheetListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/timesheets`,
        params,
      }),
      providesTags: ['Timesheet'],
    }),

    // Get single timesheet
    getTimesheet: builder.query<ApiResponse<Timesheet>, { projectId: string; timesheetId: string }>(
      {
        query: ({ projectId, timesheetId }) => ({
          url: `/projects/${projectId}/timesheets/${timesheetId}`,
        }),
        providesTags: (_result, _error, { timesheetId }) => [
          { type: 'Timesheet', id: timesheetId },
        ],
      }
    ),

    // Create timesheet
    createTimesheet: builder.mutation<
      ApiResponse<Timesheet>,
      { projectId: string; data: CreateTimesheetRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/timesheets`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Timesheet'],
    }),

    // Update timesheet
    updateTimesheet: builder.mutation<
      ApiResponse<Timesheet>,
      { projectId: string; timesheetId: string; data: UpdateTimesheetRequest }
    >({
      query: ({ projectId, timesheetId, data }) => ({
        url: `/projects/${projectId}/timesheets/${timesheetId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { timesheetId }) => [
        'Timesheet',
        { type: 'Timesheet', id: timesheetId },
      ],
    }),

    // Delete timesheet
    deleteTimesheet: builder.mutation<
      ApiResponse<void>,
      { projectId: string; timesheetId: string }
    >({
      query: ({ projectId, timesheetId }) => ({
        url: `/projects/${projectId}/timesheets/${timesheetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Timesheet'],
    }),

    // ==========================================================================
    // STATS
    // ==========================================================================

    // Get timesheet stats
    getTimesheetStats: builder.query<
      ApiResponse<TimesheetStats>,
      { projectId: string; weekOf?: string }
    >({
      query: ({ projectId, weekOf }) => ({
        url: `/projects/${projectId}/timesheets/stats`,
        params: weekOf ? { weekOf } : {},
      }),
      providesTags: ['Timesheet'],
    }),

    // ==========================================================================
    // WORKFLOW ACTIONS
    // ==========================================================================

    // Submit timesheet for approval
    submitTimesheet: builder.mutation<
      ApiResponse<Timesheet>,
      { projectId: string; timesheetId: string; notes?: string }
    >({
      query: ({ projectId, timesheetId, notes }) => ({
        url: `/projects/${projectId}/timesheets/${timesheetId}/submit`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: (_result, _error, { timesheetId }) => [
        'Timesheet',
        { type: 'Timesheet', id: timesheetId },
      ],
    }),

    // Approve timesheet
    approveTimesheet: builder.mutation<
      ApiResponse<Timesheet>,
      { projectId: string; timesheetId: string; notes?: string }
    >({
      query: ({ projectId, timesheetId, notes }) => ({
        url: `/projects/${projectId}/timesheets/${timesheetId}/approve`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: (_result, _error, { timesheetId }) => [
        'Timesheet',
        { type: 'Timesheet', id: timesheetId },
      ],
    }),

    // Reject timesheet
    rejectTimesheet: builder.mutation<
      ApiResponse<Timesheet>,
      { projectId: string; timesheetId: string; reason: string }
    >({
      query: ({ projectId, timesheetId, reason }) => ({
        url: `/projects/${projectId}/timesheets/${timesheetId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { timesheetId }) => [
        'Timesheet',
        { type: 'Timesheet', id: timesheetId },
      ],
    }),

    // ==========================================================================
    // EMPLOYEE RATES
    // ==========================================================================

    // Get employee rates
    getEmployeeRates: builder.query<
      ApiResponse<EmployeeRate[]>,
      { projectId: string; employeeId: string }
    >({
      query: ({ projectId, employeeId }) => ({
        url: `/projects/${projectId}/timesheets/employee-rates/${employeeId}`,
      }),
      providesTags: ['Timesheet'],
    }),

    // Set employee rate
    setEmployeeRate: builder.mutation<
      ApiResponse<EmployeeRate>,
      { projectId: string; data: SetEmployeeRateRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/timesheets/employee-rates`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Timesheet'],
    }),

    // ==========================================================================
    // LABOR REPORT
    // ==========================================================================

    // Get labor cost report
    getLaborReport: builder.query<
      ApiResponse<LaborReport>,
      { projectId: string; params: LaborReportParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/timesheets/labor-report`,
        params,
      }),
      providesTags: ['Timesheet'],
    }),
  }),
});

export const {
  useGetTimesheetsQuery,
  useGetTimesheetQuery,
  useCreateTimesheetMutation,
  useUpdateTimesheetMutation,
  useDeleteTimesheetMutation,
  useGetTimesheetStatsQuery,
  useSubmitTimesheetMutation,
  useApproveTimesheetMutation,
  useRejectTimesheetMutation,
  useGetEmployeeRatesQuery,
  useSetEmployeeRateMutation,
  useGetLaborReportQuery,
} = timesheetApi;
